import { useRef, useState } from "react";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import dayjs from "dayjs";
import { InboundLayout, InboundHeaderCard } from "ui-components/Layout";
import { InboundDetailAddButton, InboundDetailEditButton } from "ui-components/Form";

import { headerRender, headerValues, headerValidation } from "./receiving-schema";
import { detailRender, detailValidation, detailValues } from "./receiving-details-schema";
import Receiving from "./components/Receiving";
import ReceivingDetail from "./components/ReceivingDetail";
import { useCustomerValue } from "providers/CustomerContext";

import { recvColumns } from "./columns";
import { InfoDialog, Success, Error_ } from "ui-components/Alert";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createReceiving, getVDONo, getRecvWork } from "services/data.inbound.service";

import BasicTable2 from "ui-components/tables/BasicTable2";
import ApplyAll from "../components/ApplyAll";
import {
  deleteDetail,
  getTotalQuantity,
  recvSkuValidation_,
  serialValidation_,
  updateDetail,
} from "../common/common";
import { RecvCaption } from "../components/Caption";
import { useVendorOptionsWithName } from "hooks/useQueryHook";
import { useNavigate } from "react-router-dom";
import RecvSerialNumber from "./components/RecvSerialNumber";
import ReceivingDivisionGroup from "./components/ReceivingDivisionGroup";
import { getShowDivision } from "services/data.inbound.service";
import { LoadingDialog } from "ui-components/Alert";

const defaultValues = {
  ...headerValues,
  dateReceived: dayjs(new Date()).format("YYYY-MM-DD"),
  status: "Open",
  recvType: "MISC Receipt",
};

function AddReceiving() {
  const { customerCode } = useCustomerValue();
  const navigate = useNavigate();
  const formValues = { ...defaultValues, customerCode };
  const [details, setDetails] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [vDONoOptions, setVDONoOtpions] = useState([]);
  const [click, setClick] = useState(false);
  const [alert, setAlert] = useState(null);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const [uploadData, setUploadData] = useState([]);
  const [showDivision, setShowDivision] = useState(false);

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });
  const {
    control,
    watch,
    formState: { errors },
    setValue,
    getValues,
    trigger,
  } = form;

  const [vendorCode, vDONo] = watch(["vendorCode", "vDONo"]);

  useVendorOptionsWithName({
    customerCode,
    onSuccess: (data) => {
      const vendors = data.data.data;
      if (vendors.length > 0) {
        setValue("vendorCode", vendors[0].vendorCode);
        setValue("sellerCompany", vendors[0].vendShortName);
      }
      setVendorOptions(vendors.map((x) => x.vendorCode));
    },
    onError,
  });

  const createReceivingMutation = useMutation(createReceiving, {});

  const createStatus = createReceivingMutation.status === "loading";

  useQuery({
    queryKey: ["show-division", customerCode],
    queryFn: () => getShowDivision({ customerCode }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => setShowDivision(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["vdono", vendorCode],
    queryFn: () => getVDONo({ vendorCode }),
    enabled: Boolean(customerCode) && Boolean(vendorCode),
    onSuccess: (data) => setVDONoOtpions(data.data.data),
    onError,
  });

  const {isFetching} = useQuery({
    queryKey: ["recv-work", vDONo],
    queryFn: () => getRecvWork({ customerCode, vendorCode, vDONo }),
    enabled: Boolean(customerCode) && Boolean(vendorCode) && Boolean(vDONo),
    onSuccess: (data) => {
      setAlert(null)
      setDetails(data.data.data)
    },
    onError,
    retry:false
  });

  const headerProps = {
    headerRender,
    customerCode,
    control,
    setValue,
    errors,
    mode: "ADD",
    vendorOptions,
    vDONoOptions,
  };

  const detailProps = {
    details,
    setDetails,
    uploadData,
    setUploadData,
    showDivision,
    values: { customerCode, vendorCode },
  };

  const onSubmit = async (generatePalletize) => {
    setClick(true);
    window.scrollTo(0, 0);
    const isValid = await trigger();
    if (!isValid) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      setClick(false);
      return;
    }

    if (details.length === 0) {
      setAlert(<Error_ content="Invalid Receiving Details" onChange={setAlert} />);
      setClick(false);
      return;
    }

    const serialNumbersMap = new Map();
    const affectedDets = new Set();

    for (const det of details) {
      for (const serial of det.serialNumbers) {
        const { serialNumber } = serial;
        if (serialNumbersMap.has(serialNumber)) {
          affectedDets.add(det.lineNo);
          affectedDets.add(serialNumbersMap.get(serialNumber).lineNo);
        } else {
          serialNumbersMap.set(serialNumber, det);
        }
      }
    }
    const result = Array.from(affectedDets);
    if (result.length > 0) {
      setAlert(
        <Error_
          content={`Duplicate Serial Numbers in LineNo ${result.join(", ")}`}
          onChange={setAlert}
        />
      );
      setClick(false);
      return;
    }

    const notTally = details.filter(
      (x) => x.inbSerialNbrCtrl && x.quantity !== x.serialNumbers.length
    );
    if (notTally.length > 0) {
      setAlert(
        <Error_
          content={`Serial Numbers Count Not Same as Quantity in LineNo ${notTally.join(", ")}`}
          onChange={setAlert}
        />
      );
      setClick(false);
      return;
    }

    const data = getValues();

    const recvDetails = details.map((detail) => ({
      ...detail,
      status: "O",
      uom: detail.uom.label,
    }));

    const req = {
      ...data,
      divisionQuantity: JSON.stringify(uploadData),
      generatePalletize,
      recvDetails,
    };

    createReceivingMutation.mutate(req, {
      onSuccess: (data) => navigate("/inbound/receiving", { state: data.data.message }),
      onError: (data) => {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        setAlert(<Error_ data={data} onChange={setAlert} />);
      },
      onSettled: () => setClick(false),
    });
  };

  return (
    <InboundLayout lg={12}>
      {alert}
      {isFetching && <LoadingDialog/>}
      <InboundHeaderCard>
        <form noValidate>
          <Receiving {...headerProps} />
        </form>
      </InboundHeaderCard>

      <Detail {...detailProps} />

      <MDBox mt={2} display="flex" justifyContent="center">
        <MDButton
          variant="gradient"
          color="info"
          disabled={details.length === 0 || createStatus || click}
          onClick={() => onSubmit(false)}
        >
          Submit
        </MDButton>
        <MDButton
          sx={{ marginLeft: "1.5rem" }}
          variant="outlined"
          color="info"
          disabled={(showDivision && uploadData.length===0) || details.length === 0 || createStatus || click}
          onClick={() => onSubmit(true)}
        >
          Palletize
        </MDButton>
      </MDBox>
    </InboundLayout>
  );
}

const Detail = ({ details, setDetails, uploadData, setUploadData, showDivision, values }) => {
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      renderActions: ["Delete"],
      type: "actions",
    },
    ...recvColumns,
  ];

  const [alert, setAlert] = useState(null);
  const [serialAlert, setSerialAlert] = useState(null);

  const [formValues, setFormValues] = useState(null);
  const [dialog, setDialog] = useState(null);

  const lineNo = formValues ? formValues.lineNo : details.length + 1;

  const doRef = useRef(null);
  const invRef = useRef(null);

  const form = useForm({
    defaultValues: { ...detailValues, lineNo, serialNumbers: [] },
    values: formValues || {
      ...detailValues,
      lineNo,
      status: "Open",
      vDONo: doRef.current,
      vInvNo: invRef.current,
      serialNumbers: [],
    },
    resolver: yupResolver(detailValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isDirty },
    setValue,
    getValues,
    handleSubmit,
    reset,
  } = form;
  const [skuCode, recvWh, recvZone] = watch(["skuCode", "recvWh", "recvZone"]);

  const onSubmit = (data) => {
    setSerialAlert(null);
    const isSkuAnyDuplicate = recvSkuValidation_(details, data);
    if (isSkuAnyDuplicate) {
      setAlert(<Error_ content="Duplicate SKU Code" onChange={setAlert} />);
      return;
    }

    if (data.inbSerialNbrCtrl && data.quantity !== data.serialNumbers.length) {
      const errorMsg =
        data.quantity > data.serialNumbers.length
          ? `Not Enough Serial Number(${data.serialNumbers.length})`
          : `Too Many Serial Number(${data.serialNumbers.length})`;
      setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
      return;
    }
    const isSerialAnyDuplicate = serialValidation_(details, data);
    if (isSerialAnyDuplicate.isValid) {
      if (isSerialAnyDuplicate.type === "Single") {
        const errorMsg = "Duplicate Serial Number " + isSerialAnyDuplicate.serialNumbers.join(", ");
        setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
        return;
      } else if (isSerialAnyDuplicate.type === "Multiple") {
        const errorMsg =
          "Duplicate Serial Number " +
          isSerialAnyDuplicate.serialNumbers.join(", ") +
          " In LineNo " +
          isSerialAnyDuplicate.lineNos.join(", ");
        setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
        return;
      }
    }

    if (formValues === null) {
      doRef.current = data.vDONo;
      invRef.current = data.vInvNo;
      setDetails([...details, { ...data }]);
      reset();
      setAlert(<Success content="Added" onChange={setAlert} />);
    } else {
      setDetails(updateDetail(details, data));
      setFormValues(null);
      setAlert(<Success content="Updated" onChange={setAlert} />);
    }
  };
  const onRowClick = (values) => {
    setSerialAlert(null);
    if (formValues !== null && isDirty) {
      const data = getValues();
      const isSkuAnyDuplicate = recvSkuValidation_(details, data);
      if (isSkuAnyDuplicate) {
        setAlert(<Error_ content="Duplicate SKU Code" onChange={setAlert} />);
        return false;
      }
      //check current serial number, if invalid then keep focus on
      if (data.inbSerialNbrCtrl && data.quantity !== data.serialNumbers.length) {
        const errorMsg =
          data.quantity > data.serialNumbers.length
            ? `Not Enough Serial Number(${data.serialNumbers.length})`
            : `Too Many Serial Number(${data.serialNumbers.length})`;
        setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
        return false;
      }
      const isSerialAnyDuplicate = serialValidation_(details, data);
      if (isSerialAnyDuplicate.isValid) {
        if (isSerialAnyDuplicate.type === "Single") {
          const errorMsg =
            "Duplicate Serial Number " + isSerialAnyDuplicate.serialNumbers.join(", ");
          setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
          return false;
        } else if (isSerialAnyDuplicate.type === "Multiple") {
          const errorMsg =
            "Duplicate Serial Number " +
            isSerialAnyDuplicate.serialNumbers.join(", ") +
            " In LineNo " +
            isSerialAnyDuplicate.lineNos.join(", ");
          setSerialAlert(<Error_ content={errorMsg} onChange={setSerialAlert} />);
          return false;
        }
      }

      setDialog(
        <InfoDialog
          title={`Unsaved changes for Line No ${formValues.lineNo}`}
          content="Do you want to save the changes"
          onOk={() => {
            setDetails(updateDetail(details, data));
            setDialog(null);
            setFormValues(values);
          }}
          onClose={() => {
            setDialog(null);
            setFormValues(values);
          }}
        />
      );
    } else {
      setFormValues(values);
    }
    return true;
  };
  const handleDelete = (data) => {
    setDetails(deleteDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Deleted" onChange={setAlert} />);
  };

  const detailProps = {
    detailRender,
    values: { ...values, skuCode, recvWh, recvZone },
    control,
    errors,
    setValue,
    getValues,
    serialAlert,
  };
  const totalQuantity = getTotalQuantity(details, "quantity");
  const caption = <RecvCaption totalRows={details.length} totalQuantity={totalQuantity} />;

  const applyAllProps = { formValues, setFormValues, setValue, details, setDetails };

  return (
    <>
      <InboundHeaderCard>
        {alert}
        {dialog}
        <ApplyAll {...applyAllProps} />

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <ReceivingDetail {...detailProps} />
          {formValues === null ? (
            <InboundDetailAddButton />
          ) : (
            <InboundDetailEditButton disabled={!isDirty} onClick={() => setFormValues(null)} />
          )}
        </form>

        <BasicTable2
          columns={columns}
          caption={caption}
          rows={details}
          onRowClick={onRowClick}
          onDelete={handleDelete}
        />
      </InboundHeaderCard>
      {showDivision && (
        <ReceivingDivisionGroup
          details={details}
          uploadData={uploadData}
          setUploadData={setUploadData}
        />
      )}
    </>
  );
};

export default AddReceiving;
